// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what a room does together beyond following its lead
 * (`docs/MEETINGS_COLLAB_PLAN.md`): an **activity** the lead starts —
 * free exploration with pins and a heat map of where people look, a
 * question answered by pointing at the globe, or a choice — and the
 * small messages each one needs.
 *
 * AYNI (owner, 2026-10-12): a **poll** (no right answer: everyone sees the
 * counts as they come, one answer or several), and what a meeting keeps of
 * its people until it ends — who is in it (a number each, and the name they
 * gave), and each person's answers activity by activity (`RoomPerson`,
 * `LoggedActivity`, `Given`). Nothing of it leaves the meeting's room.
 *
 * Like `roomProtocol.ts`, which carries these messages, it is shapes,
 * limits and validation only, imported by the browser and by the room's
 * Durable Object (`workers/rooms`). Everything that arrives is checked
 * here before either side acts on it, and every limit the room enforces
 * is named here, so the interface can say it and the tests can pin it.
 */

export type ActivityKind = 'explore' | 'point' | 'choice' | 'poll'

/** What the lead asks for when starting an activity. */
export interface ActivityStart {
  kind: ActivityKind
  /** What the presenter asks of the room. */
  prompt: string
  /**
   * How long it runs; null for no limit. An explore ends itself when time
   * is up; a question stops taking answers and waits for the reveal.
   */
  durationMs: number | null
  /** The options of a `choice` or a `poll`; null otherwise. */
  options: string[] | null
  /** A poll that takes several answers from each person. */
  multi?: boolean
}

/** An activity as the room tells it. */
export interface RoomActivity {
  id: string
  kind: ActivityKind
  prompt: string
  /** Milliseconds left when the room sent it; null for no limit. */
  remainingMs: number | null
  /** Milliseconds since it started, when the room sent it; null from a room that does not say. */
  elapsedMs: number | null
  options: string[] | null
  /** A poll that takes several answers from each person. */
  multi?: boolean
  /** A question that has been revealed (a poll: closed): no more answers, results follow. */
  closed: boolean
}

/** A pin someone dropped while exploring. */
export interface RoomPin {
  id: string
  lat: number
  lon: number
  note: string
  /** The display name its author gave, if any. */
  name: string | null
}

/** Where people are looking: cell centres and how many look there. */
export type LookCell = [lat: number, lon: number, count: number]

/** How a question is going, for the lead and the moderator (a poll's, for everyone). */
export interface RoomTally {
  id: string
  answered: number
  /** Per option, for a `choice` or a `poll`; null for a `point`. */
  counts: number[] | null
}

/** Someone with a hand up, for the presenter: their connection id and the name they gave. */
export interface RoomHand {
  id: string
  name: string | null
}

/** Who has the globe, when the presenter has handed it over; null when the presenter has it. */
export interface RoomSpotlight {
  name: string | null
  /** AYNI (owner, 2026-10-12): the presenter has taken over for a moment; the globe goes back with Hand back. */
  override?: boolean
}

/** Most raised hands the presenter is sent at once, oldest first. */
export const MAX_HANDS_SHOWN = 50

export function parseHands(raw: unknown): RoomHand[] | null {
  if (!Array.isArray(raw)) return null
  const hands: RoomHand[] = []
  for (const item of raw as unknown[]) {
    if (typeof item !== 'object' || item === null) return null
    const h = item as Record<string, unknown>
    if (typeof h.id !== 'string' || h.id.length === 0 || h.id.length > 64) return null
    hands.push({ id: h.id, name: cleanText(h.name, NAME_CHARS) })
  }
  return hands
}

export function parseSpotlight(raw: unknown): RoomSpotlight | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  return { name: cleanText(r.name, NAME_CHARS), ...(r.override === true ? { override: true } : {}) }
}

// ------------------------------------------------------------------ people (AYNI, owner 2026-10-12)

/**
 * Someone in a meeting, as the presenter and moderators see them: their
 * connection, their number in the meeting (kept through a reconnection:
 * "Guest 3"), the name they gave, their seat, and what they are doing.
 */
export interface RoomPerson {
  id: string
  n: number
  name: string | null
  seat: 'moderator' | 'audience'
  hand: boolean
  /** The globe they drive: 'main' (handed the presenter's globe), another globe's position, or null. */
  drives: 'main' | number | null
  /** Their microphone is live (only a driver's can be). */
  mic: boolean
}

/**
 * Most people a people list (and an activity's answers, person by person)
 * carries: a meeting holds at most 500 (MEETING_MAX_PEOPLE, since
 * 2026-10-17). It goes to the presenter and moderators only, about 60 KB
 * at 500.
 */
export const MAX_PEOPLE_SHOWN = 510

export function parsePeople(raw: unknown): RoomPerson[] | null {
  if (!Array.isArray(raw)) return null
  const people: RoomPerson[] = []
  for (const item of (raw as unknown[]).slice(0, MAX_PEOPLE_SHOWN)) {
    if (typeof item !== 'object' || item === null) return null
    const p = item as Record<string, unknown>
    if (typeof p.id !== 'string' || p.id.length === 0 || p.id.length > 64) return null
    if (!Number.isInteger(p.n) || (p.n as number) < 1 || (p.n as number) > 9999) return null
    const drives = p.drives === 'main' ? 'main' : Number.isInteger(p.drives) && (p.drives as number) >= 0 && (p.drives as number) < 4 ? (p.drives as number) : null
    people.push({ id: p.id, n: p.n as number, name: cleanText(p.name, NAME_CHARS), seat: p.seat === 'moderator' ? 'moderator' : 'audience', hand: p.hand === true, drives, mic: p.mic === true })
  }
  return people
}

/**
 * A person's key: a random code their browser makes once per meeting and
 * sends with every connection, so a reload or a dropped connection comes
 * back as the same person (their number, name and answers), and a person
 * removed from the meeting stays out on that device. Never shown.
 */
const PERSON_KEY = /^[\w-]{16,40}$/

export function isPersonKey(v: unknown): v is string {
  return typeof v === 'string' && PERSON_KEY.test(v)
}

export function newPersonKey(random: () => number = Math.random): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let key = ''
  for (let i = 0; i < 24; i++) key += alphabet[Math.floor(random() * alphabet.length)]
  return key
}

/** What one person gave in one activity: a place, an option, several (a poll), or pins (an explore). */
export interface Given {
  p?: [number, number]
  choice?: number
  choices?: number[]
  pins?: { lat: number; lon: number; note: string }[]
}

/** An activity of this meeting, as its log keeps it. */
export interface LoggedActivity {
  id: string
  kind: ActivityKind
  prompt: string
  options: string[] | null
  multi?: boolean
  /** A point question's right place, once revealed; null otherwise. */
  answer: [number, number] | null
  /** How many people gave something. */
  answered: number
}

/** One person's line in an activity's answers, for the presenter and moderators (never a poll's: a poll is anonymous). */
export interface LogRow {
  n: number
  name: string | null
  given: Given
}

/** This person's own answer in one activity, for "My answers". */
export interface MyAnswer {
  activity: LoggedActivity
  given: Given
}

/** Activities a meeting's log keeps, oldest dropped first. */
export const SESSION_MAX_ACTIVITIES = 30

export function parseGiven(raw: unknown): Given | null {
  if (typeof raw !== 'object' || raw === null) return null
  const g = raw as Record<string, unknown>
  const out: Given = {}
  if (g.p !== undefined) {
    const p = parsePoint(g.p)
    if (!p) return null
    out.p = p
  }
  if (g.choice !== undefined) {
    if (!Number.isInteger(g.choice) || (g.choice as number) < 0 || (g.choice as number) >= ACTIVITY_MAX_OPTIONS) return null
    out.choice = g.choice as number
  }
  if (g.choices !== undefined) {
    const c = parseChoices(g.choices)
    if (!c) return null
    out.choices = c
  }
  if (g.pins !== undefined) {
    if (!Array.isArray(g.pins)) return null
    out.pins = []
    for (const pin of (g.pins as unknown[]).slice(0, PINS_PER_PERSON)) {
      const q = pin as Record<string, unknown> | null
      const at = q && typeof q === 'object' ? parsePoint([q.lat, q.lon]) : null
      if (!at) return null
      out.pins.push({ lat: at[0], lon: at[1], note: cleanText(q!.note, PIN_NOTE_CHARS) ?? '' })
    }
  }
  return out
}

/** A poll's answers: distinct option indexes, at least one. */
export function parseChoices(raw: unknown): number[] | null {
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > ACTIVITY_MAX_OPTIONS) return null
  const out = new Set<number>()
  for (const c of raw as unknown[]) {
    if (!Number.isInteger(c) || (c as number) < 0 || (c as number) >= ACTIVITY_MAX_OPTIONS) return null
    out.add(c as number)
  }
  return [...out].sort((a, b) => a - b)
}

export function parseLoggedActivity(raw: unknown): LoggedActivity | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  if (!isId(r.id) || !isKind(r.kind)) return null
  const prompt = cleanText(r.prompt, ACTIVITY_PROMPT_CHARS)
  if (!prompt) return null
  const options = Array.isArray(r.options) ? (r.options as unknown[]).slice(0, ACTIVITY_MAX_OPTIONS).map((o) => cleanText(o, ACTIVITY_OPTION_CHARS) ?? '') : null
  const answer = r.answer === null || r.answer === undefined ? null : parsePoint(r.answer)
  const answered = Number.isInteger(r.answered) && (r.answered as number) >= 0 ? (r.answered as number) : 0
  return { id: r.id, kind: r.kind, prompt, options, ...(r.multi === true ? { multi: true } : {}), answer, answered }
}

export function parseLog(raw: unknown): LoggedActivity[] | null {
  if (!Array.isArray(raw)) return null
  const out: LoggedActivity[] = []
  for (const item of (raw as unknown[]).slice(-SESSION_MAX_ACTIVITIES)) {
    const a = parseLoggedActivity(item)
    if (!a) return null
    out.push(a)
  }
  return out
}

export function parseLogRows(raw: unknown): LogRow[] | null {
  if (!Array.isArray(raw)) return null
  const out: LogRow[] = []
  for (const item of (raw as unknown[]).slice(0, MAX_PEOPLE_SHOWN)) {
    const r = item as Record<string, unknown> | null
    if (typeof r !== 'object' || r === null || !Number.isInteger(r.n) || (r.n as number) < 1) return null
    const given = parseGiven(r.given)
    if (!given) return null
    out.push({ n: r.n as number, name: cleanText(r.name, NAME_CHARS), given })
  }
  return out
}

export function parseMine(raw: unknown): MyAnswer[] | null {
  if (!Array.isArray(raw)) return null
  const out: MyAnswer[] = []
  for (const item of (raw as unknown[]).slice(-SESSION_MAX_ACTIVITIES)) {
    const r = item as Record<string, unknown> | null
    const activity = r && typeof r === 'object' ? parseLoggedActivity(r.activity) : null
    const given = r && typeof r === 'object' ? parseGiven(r.given) : null
    if (!activity || !given) return null
    out.push({ activity, given })
  }
  return out
}

/** A revealed question's results (a closed poll's), for everyone. */
export type RoomResults =
  | { id: string; kind: 'point'; answer: [number, number] | null; points: [number, number][] }
  | { id: string; kind: 'choice'; options: string[]; counts: number[] }
  | { id: string; kind: 'poll'; options: string[]; counts: number[]; voters: number }

// ------------------------------------------------------------------ limits

export const ACTIVITY_PROMPT_CHARS = 200
export const ACTIVITY_OPTION_CHARS = 60
export const ACTIVITY_MIN_OPTIONS = 2
export const ACTIVITY_MAX_OPTIONS = 6
/** Longest exploration, and the durations the interface offers. */
export const ACTIVITY_MAX_DURATION_MS = 60 * 60 * 1000
export const EXPLORE_DURATIONS_MIN = [1, 3, 5, 10] as const
/** The limits offered for a question or a choice, in seconds. */
export const QUESTION_DURATIONS_S = [30, 60, 120, 300] as const
/** A participant reports where they look at most this often. */
export const LOOK_EVERY_MS = 2000
/** Pins one person may drop in one activity. */
export const PINS_PER_PERSON = 5
/** Pins one activity holds in all, so the list the lead gets stays one small message. */
export const ACTIVITY_MAX_PINS = 100
export const PIN_NOTE_CHARS = 140
/** Times one person may change an answer in one activity. */
export const ANSWER_CHANGES = 20
export const NAME_CHARS = 40
/** Looks are binned into cells this many degrees across before the lead sees them. */
export const LOOK_CELL_DEG = 3
/** The room sends the lead the heat map and the tally at most this often. */
export const ACTIVITY_FLUSH_MS = 2000

// ------------------------------------------------------------------ helpers

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/**
 * Characters that are not text to show: control characters, and the
 * invisible ones that reorder or hide text (zero-width, bidirectional
 * overrides and isolates, line and paragraph separators). Built from
 * code points so none of them sits in this source file.
 */
const NOT_TEXT = new RegExp(
  `[${[[0x00, 0x1f], [0x7f, 0x9f], [0x200b, 0x200f], [0x2028, 0x202e], [0x2066, 0x2069], [0xfeff, 0xfeff]]
    .map(([from, to]) => `${String.fromCharCode(from)}-${String.fromCharCode(to)}`)
    .join('')}]`,
  'g',
)

/** Text a person typed, made safe to show: no control or invisible characters, spaces collapsed, cut to `max`. */
export function cleanText(raw: unknown, max: number): string | null {
  if (typeof raw !== 'string') return null
  const text = raw.replace(NOT_TEXT, ' ').replace(/\s+/g, ' ').trim()
  return text.length === 0 ? null : text.slice(0, max)
}

/** A [lat, lon] pair on the Earth, rounded to about a kilometre, or null. */
export function parsePoint(raw: unknown): [number, number] | null {
  if (!Array.isArray(raw) || raw.length !== 2) return null
  const [lat, lon] = raw as unknown[]
  if (!finite(lat) || !finite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 540) return null
  const wrapped = ((((lon + 180) % 360) + 360) % 360) - 180
  return [Math.round(lat * 100) / 100, Math.round(wrapped * 100) / 100]
}

/** Great-circle distance between two [lat, lon] points, in kilometres. */
export function distanceKm(a: readonly [number, number], b: readonly [number, number]): number {
  const rad = Math.PI / 180
  const dLat = (b[0] - a[0]) * rad
  const dLon = (b[1] - a[1]) * rad
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * rad) * Math.cos(b[0] * rad) * Math.sin(dLon / 2) ** 2
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Bin points into LOOK_CELL_DEG cells: centre and count, busiest first. */
export function binLooks(points: Iterable<readonly [number, number]>, cellDeg = LOOK_CELL_DEG): LookCell[] {
  const cells = new Map<string, LookCell>()
  for (const [lat, lon] of points) {
    const i = Math.min(Math.floor((lat + 90) / cellDeg), Math.floor(180 / cellDeg) - 1)
    const j = Math.min(Math.floor((lon + 180) / cellDeg), Math.floor(360 / cellDeg) - 1)
    const key = `${i}:${j}`
    const cell = cells.get(key)
    if (cell) cell[2]++
    else cells.set(key, [-90 + (i + 0.5) * cellDeg, -180 + (j + 0.5) * cellDeg, 1])
  }
  return [...cells.values()].sort((a, b) => b[2] - a[2])
}

/** A fresh activity or pin id. */
export function newActivityId(random: () => number = Math.random): string {
  let id = ''
  for (let i = 0; i < 10; i++) id += Math.floor(random() * 36).toString(36)
  return id
}

const isId = (v: unknown): v is string => typeof v === 'string' && /^[\w-]{1,40}$/.test(v)
const isKind = (v: unknown): v is ActivityKind => v === 'explore' || v === 'point' || v === 'choice' || v === 'poll'
/** The kinds that offer options. */
export const hasOptions = (kind: ActivityKind): boolean => kind === 'choice' || kind === 'poll'

// ------------------------------------------------------------------ parsing

/** What a lead asked to start, made safe, or null when it is not one. */
export function parseActivityStart(raw: unknown): ActivityStart | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  if (!isKind(r.kind)) return null
  const prompt = cleanText(r.prompt, ACTIVITY_PROMPT_CHARS)
  if (!prompt) return null
  let durationMs: number | null = null
  if (r.durationMs !== null && r.durationMs !== undefined) {
    if (!finite(r.durationMs) || r.durationMs < 10_000 || r.durationMs > ACTIVITY_MAX_DURATION_MS) return null
    durationMs = Math.round(r.durationMs)
  }
  let options: string[] | null = null
  if (hasOptions(r.kind)) {
    if (!Array.isArray(r.options)) return null
    const cleaned = (r.options as unknown[]).map((o) => cleanText(o, ACTIVITY_OPTION_CHARS))
    if (cleaned.some((o) => o === null)) return null
    options = cleaned as string[]
    if (options.length < ACTIVITY_MIN_OPTIONS || options.length > ACTIVITY_MAX_OPTIONS) return null
  }
  return { kind: r.kind, prompt, durationMs, options, ...(r.kind === 'poll' && r.multi === true ? { multi: true } : {}) }
}

/** An activity as the room told it, or null. */
export function parseRoomActivity(raw: unknown): RoomActivity | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  if (!isId(r.id) || !isKind(r.kind) || typeof r.closed !== 'boolean') return null
  const prompt = cleanText(r.prompt, ACTIVITY_PROMPT_CHARS)
  if (!prompt) return null
  const remainingMs = r.remainingMs === null ? null : finite(r.remainingMs) && r.remainingMs >= 0 ? r.remainingMs : undefined
  if (remainingMs === undefined) return null
  const elapsedMs = finite(r.elapsedMs) && r.elapsedMs >= 0 ? r.elapsedMs : null
  let options: string[] | null = null
  if (hasOptions(r.kind)) {
    if (!Array.isArray(r.options)) return null
    options = (r.options as unknown[]).map((o) => cleanText(o, ACTIVITY_OPTION_CHARS) ?? '')
    if (options.length < ACTIVITY_MIN_OPTIONS || options.length > ACTIVITY_MAX_OPTIONS) return null
  }
  return { id: r.id, kind: r.kind, prompt, remainingMs, elapsedMs, options, ...(r.kind === 'poll' && r.multi === true ? { multi: true } : {}), closed: r.closed }
}

export function parsePins(raw: unknown): RoomPin[] | null {
  if (!Array.isArray(raw)) return null
  const pins: RoomPin[] = []
  for (const item of raw as unknown[]) {
    if (typeof item !== 'object' || item === null) return null
    const p = item as Record<string, unknown>
    const at = parsePoint([p.lat, p.lon])
    if (!isId(p.id) || !at) return null
    pins.push({ id: p.id, lat: at[0], lon: at[1], note: cleanText(p.note, PIN_NOTE_CHARS) ?? '', name: cleanText(p.name, NAME_CHARS) })
  }
  return pins
}

export function parseLookCells(raw: unknown): LookCell[] | null {
  if (!Array.isArray(raw)) return null
  const cells: LookCell[] = []
  for (const c of raw as unknown[]) {
    if (!Array.isArray(c) || c.length !== 3) return null
    const [lat, lon, n] = c as unknown[]
    if (!finite(lat) || !finite(lon) || !finite(n) || Math.abs(lat) > 90 || Math.abs(lon) > 180 || n < 1) return null
    cells.push([lat, lon, n])
  }
  return cells
}

export function parseTally(raw: unknown): RoomTally | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  if (!isId(r.id) || !finite(r.answered) || r.answered < 0) return null
  const counts = r.counts === null ? null : Array.isArray(r.counts) && r.counts.every((n) => finite(n) && n >= 0) ? (r.counts as number[]) : undefined
  if (counts === undefined) return null
  return { id: r.id, answered: r.answered, counts }
}

export function parseResults(raw: unknown): RoomResults | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  if (!isId(r.id)) return null
  if (r.kind === 'point') {
    const answer = r.answer === null ? null : parsePoint(r.answer)
    if (r.answer !== null && !answer) return null
    if (!Array.isArray(r.points)) return null
    const points = (r.points as unknown[]).map(parsePoint)
    if (points.some((p) => p === null)) return null
    return { id: r.id, kind: 'point', answer, points: points as [number, number][] }
  }
  if (r.kind === 'choice' || r.kind === 'poll') {
    if (!Array.isArray(r.options) || !Array.isArray(r.counts) || r.options.length !== r.counts.length) return null
    const options = (r.options as unknown[]).map((o) => cleanText(o, ACTIVITY_OPTION_CHARS) ?? '')
    if (!(r.counts as unknown[]).every((n) => finite(n) && n >= 0)) return null
    if (r.kind === 'poll') return { id: r.id, kind: 'poll', options, counts: r.counts as number[], voters: finite(r.voters) && r.voters >= 0 ? r.voters : 0 }
    return { id: r.id, kind: 'choice', options, counts: r.counts as number[] }
  }
  return null
}
